/**
 * @file Howler-based sound effects and looping background music. Logical names
 * are registered in `soundData` below (SFX vs `music: true` tracks). When
 * `global.IS_ON_BAEDITOR` is set, playback and most muting/volume side effects
 * are skipped. SFX vs music mute/volume are also synced from app state via
 * `UpdateCore` / vault plugins in addition to these APIs.
 */
const _ = require("lodash");
const {Howl} = require("howler");
const ErrorUtil = require("../../../shared/ErrorUtil");
const SoundsLogging = require("./SoundsLogging");

//
// --- List of sounds
//

// const fromMusicAssets = name => [`/assets/sounds/${name}.wav`, `/assets/sounds/${name}.mp3`];
const fromMusicAssets = name => [
	`/assets/sounds/${name}.opus`,
	`/assets/sounds/${name}.m4a`,
];
const fromSfxAssets = name => [`/assets/sounds/${name}.mp3`];
// Add new sounds by adding to this array. Things passed to playMusic should
// have music: true on them.
const soundData = [
	{
		name: "general-background",
		music: true,
		paths: fromMusicAssets("music/general-background"),
	},
	{name: "main-menu", music: true, paths: fromMusicAssets("music/main-menu")},
	{
		name: "lab-background",
		music: true,
		paths: fromMusicAssets("music/lab-background"),
	},
	{name: "button-click", paths: fromSfxAssets("sfx/blue-button")},
	{name: "locked-lesson", paths: fromSfxAssets("sfx/locked-lesson")},
	{name: "solution-correct", paths: fromSfxAssets("sfx/solution-correct")},
	{name: "solution-incorrect", paths: fromSfxAssets("sfx/solution-incorrect")},
	{name: "stars-1", paths: fromSfxAssets("sfx/stars-1")},
	{name: "stars-2", paths: fromSfxAssets("sfx/stars-2")},
	{name: "stars-3", paths: fromSfxAssets("sfx/stars-3")},
	{name: "logout", paths: fromSfxAssets("sfx/logout")},
	{name: "lesson-click", paths: fromSfxAssets("sfx/lesson-click")},
	{name: "erase", paths: fromSfxAssets("sfx/erase")},
	{name: "arrow-left", paths: fromSfxAssets("sfx/arrow-left")},
	{name: "arrow-right", paths: fromSfxAssets("sfx/arrow-right")},
	{name: "lesson-menu", paths: fromSfxAssets("sfx/lesson-menu")},
	{name: "squishy", paths: fromSfxAssets("sfx/squishy")},
	{name: "book-section-click", paths: fromSfxAssets("sfx/book-section-click")},
	{name: "calendar-tab-click", paths: fromSfxAssets("sfx/calendar-tab-click")},
	{name: "top-bar-icon-1", paths: fromSfxAssets("sfx/top-bar-icon-1")},
	{name: "top-bar-icon-2", paths: fromSfxAssets("sfx/top-bar-icon-2")},
	{name: "top-bar-icon-3", paths: fromSfxAssets("sfx/top-bar-icon-3")},
	{name: "folders-1", paths: fromSfxAssets("sfx/folders-1")},
	{name: "folders-2", paths: fromSfxAssets("sfx/folders-2")},
	{name: "folders-3", paths: fromSfxAssets("sfx/folders-3")},
	{name: "folders-4", paths: fromSfxAssets("sfx/folders-4")},
	{name: "frog-burp", paths: fromSfxAssets("sfx/frog-burp")},
	{name: "test-start", paths: fromSfxAssets("sfx/test-start")},
	{name: "keyboard-click", paths: fromSfxAssets("sfx/keyboard-click")},
	{name: "drawing-tools", paths: fromSfxAssets("sfx/drawing-tools")},
	{name: "thud", paths: fromSfxAssets("sfx/thud")},
	{name: "whoosh", paths: fromSfxAssets("sfx/whoosh")},
	{name: "navigate-problem", paths: fromSfxAssets("sfx/navigate-problem")},
	{name: "x-button", paths: fromSfxAssets("sfx/x-button")},
	{name: "level-up", paths: fromSfxAssets("sfx/level-up")},
	// Summary page character themes
	{
		name: "fiona-stars-0",
		paths: fromSfxAssets("character-summary/fiona-stars-0"),
	},
	{
		name: "fiona-stars-1",
		paths: fromSfxAssets("character-summary/fiona-stars-1"),
	},
	{
		name: "fiona-stars-2",
		paths: fromSfxAssets("character-summary/fiona-stars-2"),
	},
	{
		name: "fiona-stars-3",
		paths: fromSfxAssets("character-summary/fiona-stars-3"),
	},
	{
		name: "grok-stars-0",
		paths: fromSfxAssets("character-summary/grok-stars-0"),
	},
	{
		name: "grok-stars-1",
		paths: fromSfxAssets("character-summary/grok-stars-1"),
	},
	{
		name: "grok-stars-2",
		paths: fromSfxAssets("character-summary/grok-stars-2"),
	},
	{
		name: "grok-stars-3",
		paths: fromSfxAssets("character-summary/grok-stars-3"),
	},
	{
		name: "kraken-stars-0",
		paths: fromSfxAssets("character-summary/kraken-stars-0"),
	},
	{
		name: "kraken-stars-1",
		paths: fromSfxAssets("character-summary/kraken-stars-1"),
	},
	{
		name: "kraken-stars-2",
		paths: fromSfxAssets("character-summary/kraken-stars-2"),
	},
	{
		name: "kraken-stars-3",
		paths: fromSfxAssets("character-summary/kraken-stars-3"),
	},
	{name: "q-stars-0", paths: fromSfxAssets("character-summary/q-stars-0")},
	{name: "q-stars-1", paths: fromSfxAssets("character-summary/q-stars-1")},
	{name: "q-stars-2", paths: fromSfxAssets("character-summary/q-stars-2")},
	{name: "q-stars-3", paths: fromSfxAssets("character-summary/q-stars-3")},
	{
		name: "ms_levans-stars-0",
		paths: fromSfxAssets("character-summary/ms_levans-stars-0"),
	},
	{
		name: "ms_levans-stars-1",
		paths: fromSfxAssets("character-summary/ms_levans-stars-1"),
	},
	{
		name: "ms_levans-stars-2",
		paths: fromSfxAssets("character-summary/ms_levans-stars-2"),
	},
	{
		name: "ms_levans-stars-3",
		paths: fromSfxAssets("character-summary/ms_levans-stars-3"),
	},
	{
		name: "rote-stars-0",
		paths: fromSfxAssets("character-summary/rote-stars-0"),
	},
	{
		name: "rote-stars-1",
		paths: fromSfxAssets("character-summary/rote-stars-1"),
	},
	{
		name: "rote-stars-2",
		paths: fromSfxAssets("character-summary/rote-stars-2"),
	},
	{
		name: "rote-stars-3",
		paths: fromSfxAssets("character-summary/rote-stars-3"),
	},
	{
		name: "test-stars-0",
		paths: fromSfxAssets("character-summary/test-stars-0"),
	},
	{
		name: "test-stars-1",
		paths: fromSfxAssets("character-summary/test-stars-1"),
	},
	{
		name: "test-stars-2",
		paths: fromSfxAssets("character-summary/test-stars-2"),
	},
	{
		name: "test-stars-3",
		paths: fromSfxAssets("character-summary/test-stars-3"),
	},
	// Trophy character themes
	{name: "fiona-trophy", paths: fromSfxAssets("character-trophy/fiona-trophy")},
	{name: "grok-trophy", paths: fromSfxAssets("character-trophy/grok-trophy")},
	{
		name: "kraken-trophy",
		paths: fromSfxAssets("character-trophy/kraken-trophy"),
	},
	{name: "q-trophy", paths: fromSfxAssets("character-trophy/q-trophy")},
	{name: "rote-trophy", paths: fromSfxAssets("character-trophy/rote-trophy")},
	{
		name: "ms_levans-trophy",
		paths: fromSfxAssets("character-trophy/ms_levans-trophy"),
	},
	// Character stingers
	{name: "fiona-cyw-1", paths: fromSfxAssets("character-stingers/fiona-cyw-1")},
	{
		name: "fiona-incorrect-1",
		paths: fromSfxAssets("character-stingers/fiona-incorrect-1"),
	},
	{name: "grok-cyw-1", paths: fromSfxAssets("character-stingers/grok-cyw-1")},
	{
		name: "grok-incorrect-1",
		paths: fromSfxAssets("character-stingers/grok-incorrect-1"),
	},
	{
		name: "kraken-cyw-1",
		paths: fromSfxAssets("character-stingers/kraken-cyw-1"),
	},
	{
		name: "kraken-incorrect-1",
		paths: fromSfxAssets("character-stingers/kraken-incorrect-1"),
	},
	{name: "q-cyw-1", paths: fromSfxAssets("character-stingers/q-cyw-1")},
	{
		name: "q-incorrect-1",
		paths: fromSfxAssets("character-stingers/q-incorrect-1"),
	},
	{name: "rote-cyw-1", paths: fromSfxAssets("character-stingers/rote-cyw-1")},
	{
		name: "rote-incorrect-1",
		paths: fromSfxAssets("character-stingers/rote-incorrect-1"),
	},
	{
		name: "ms_levans-cyw-1",
		paths: fromSfxAssets("character-stingers/ms_levans-cyw-1"),
	},
	{
		name: "ms_levans-incorrect-1",
		paths: fromSfxAssets("character-stingers/ms_levans-incorrect-1"),
	},
	{
		name: "headmaster-cyw-1",
		paths: fromSfxAssets("character-stingers/headmaster-cyw-1"),
	},
	{
		name: "headmaster-cyw-2",
		paths: fromSfxAssets("character-stingers/headmaster-cyw-2"),
	},
	{
		name: "headmaster-popin",
		paths: fromSfxAssets("character-stingers/headmaster-popin"),
	},
	{
		name: "headmaster-incomplete",
		paths: fromSfxAssets("character-stingers/headmaster-incomplete"),
	},
	{
		name: "headmaster-passpart1",
		paths: fromSfxAssets("character-stingers/headmaster-passpart1"),
	},
	// Puzzle lab sounds
	{
		name: "earned-comet",
		paths: fromSfxAssets("lab-sfx/earned-comet"),
	},
	{
		name: "earned-galaxy",
		paths: fromSfxAssets("lab-sfx/earned-galaxy"),
	},
	{
		name: "earned-moon",
		paths: fromSfxAssets("lab-sfx/earned-moon"),
	},
	{
		name: "earned-planet",
		paths: fromSfxAssets("lab-sfx/earned-planet"),
	},
	{
		name: "lab-cyw-1",
		paths: fromSfxAssets("lab-sfx/lab-cyw-1"),
	},
	{
		name: "lab-incorrect-1",
		paths: fromSfxAssets("lab-sfx/lab-incorrect-1"),
	},
	{
		name: "summary-comet",
		paths: fromSfxAssets("lab-sfx/summary-comet"),
	},
	{
		name: "summary-galaxy",
		paths: fromSfxAssets("lab-sfx/summary-galaxy"),
	},
	{
		name: "summary-moon",
		paths: fromSfxAssets("lab-sfx/summary-moon"),
	},
	{
		name: "summary-planet",
		paths: fromSfxAssets("lab-sfx/summary-planet"),
	},
	// CPV Sounds
	{
		// NOTE: this is currently unused
		name: "cpv-footsteps",
		paths: fromSfxAssets("sfx/footsteps"),
	},
	{
		name: "cpv-award",
		paths: fromSfxAssets("cpv-sfx/award"),
	},
	{
		name: "cpv-hop",
		paths: fromSfxAssets("cpv-sfx/hop"),
	},
	{
		name: "cpv-snap",
		paths: fromSfxAssets("cpv-sfx/snap"),
	},
	{
		name: "cpv-streak",
		paths: fromSfxAssets("cpv-sfx/streak"),
	},
];

//
// --- End list of sounds
//

const soundNameList = soundData.filter(x => !x.music).map(x => x.name);
const musicNameList = soundData.filter(x => x.music).map(x => x.name);

// Global state
const soundLookup = {};
let musicMuted = false;
let musicVolume = 1;
let currentMusic = null; // {name, id} when it exists. id is the Howler ID.
let toPlayMusic = null; // {name, timeoutID} when it exists.
let toFadeMusic = null; // {timeoutID} when it exists.
let soundMuted = false;
let soundVolume = 1;

/**
 * Returns a cached Howl for `name`, optionally creating and decoding it.
 * @param {string} name Logical sound id from `soundData`.
 * @param {boolean} [loadIfMissing=true] If false, returns null when not yet cached.
 * @returns {import("howler").Howl|null}
 */
const getSound = (name, loadIfMissing = true) => {
	if (soundLookup[name]) return soundLookup[name]; // already loaded
	if (!loadIfMissing) return null;
	if (global.IS_ON_BAEDITOR) return null;

	const data = soundData.find(d => d.name === name);
	if (!data) return null; // can't find sound

	const volume = data.music ? musicVolume : soundVolume;
	const sound = new Howl({
		src: data.paths,
		loop: !!data.music,
		volume,
		onplayerror: () => {
			soundLookup[name].once("unlock", () => {
				soundLookup[name].play();
			});
		},
	});
	sound.mute(soundMuted);
	soundLookup[name] = sound;
	return sound;
};

/**
 * Invokes a Howl instance method on every loaded sound (SFX and music Howls).
 * @param {string} funcName Method name on Howl (e.g. `"mute"`, `"volume"`).
 * @param {...*} args Arguments passed to each call.
 */
const forEachLoadedSound = (funcName, ...args) => {
	_.values(soundLookup).forEach(sound => sound[funcName](...args));
};

/**
 * Eagerly loads every entry in `soundData` so first playback has less latency.
 */
const preloadAllSoundFiles = () => {
	soundData.forEach(data => getSound(data.name));
};

/**
 * Plays a one-shot effect. Names must be SFX entries in `soundData` (not `music: true`).
 * @param {string} name SFX logical name.
 */
const playSound = name => {
	if (global.IS_ON_BAEDITOR) return;

	const sound = getSound(name);
	if (!sound) {
		ErrorUtil.log("E_MISSING_SOUND", `Unrecognized sound name ${name}`);
		return;
	}
	if (!_.includes(soundNameList, name)) {
		ErrorUtil.log(
			"E_BAD_SOUND_TYPE",
			`Called playSound for music ${name}; use playMusic`
		);
		return;
	}

	sound.play();
};

/**
 * Stops a playing SFX by name. Must not be used for music tracks.
 * @param {string} name SFX logical name.
 */
const stopSound = name => {
	if (global.IS_ON_BAEDITOR) return;

	const sound = getSound(name);
	if (!sound) {
		ErrorUtil.log(
			"E_MISSING_SOUND",
			`Tried to stop playing unrecognized sound name ${name}`
		);
		return;
	} else if (!_.includes(soundNameList, name)) {
		ErrorUtil.log(
			"E_BAD_SOUND_TYPE",
			`Called stopSound for music ${name}; use stopMusic`
		);
		return;
	}

	if (sound.playing()) {
		sound.stop();
	}
};

/**
 * @param {string} name SFX logical name.
 * @returns {boolean}
 */
const isSoundPlaying = name => {
	const sound = getSound(name);
	return sound && sound.playing();
};

/**
 * @param {string} name Logical sound name (SFX or music).
 * @returns {number} Duration in seconds, or `0` if unknown / not loaded.
 */
const getSoundDuration = name => {
	const sound = getSound(name);
	return sound ? sound.duration() : 0.0;
};

/**
 * Mutes or unmutes all loaded SFX Howls (not the separate music mute flag).
 * @param {boolean} value True to mute SFX globally at the Howler layer.
 */
const setAllSoundsMuted = value => {
	if (global.IS_ON_BAEDITOR) return;
	SoundsLogging.logItem("SET_SOUNDS_MUTED", {value});
	soundMuted = !!value;

	forEachLoadedSound("mute", soundMuted);
};

/**
 * Sets master SFX volume for all loaded one-shot Howls (`0`–`100` UI scale).
 * @param {number} volume Percent 0–100; stored internally as 0–1 gain.
 */
const setAllSoundsVolume = volume => {
	if (global.IS_ON_BAEDITOR) return;
	SoundsLogging.logItem("SET_SOUNDS_VOLUME", {volume});
	soundVolume = Math.max(0, volume / 100);

	forEachLoadedSound("volume", soundVolume);
};

/**
 * Starts or switches looping background music. Stops any current track first
 * (unless the same track is already playing without a fade). Uses a short delay
 * before starting playback. Name must be a `music: true` entry in `soundData`.
 * @param {string} name Music logical name.
 */
const playMusic = name => {
	if (global.IS_ON_BAEDITOR) return;
	SoundsLogging.logItem("PLAY_MUSIC_START");
	const newMusic = getSound(name);
	if (!newMusic) {
		ErrorUtil.log("E_MISSING_SOUND", `Unrecognized sound name ${name}`);
		return;
	}
	if (!_.includes(musicNameList, name)) {
		ErrorUtil.log(
			"E_BAD_SOUND_TYPE",
			`Called playMusic for sound ${name}; use playSound`
		);
		return;
	}
	if (currentMusic) {
		SoundsLogging.logItem("PLAY_MUSIC_ALREADY_CURRENT", {
			currentName: currentMusic.name,
			newName: name,
			toFadeMusic,
		});
		// Don't replay the same music from the beginning
		if (name === currentMusic.name && !toFadeMusic) return;
		SoundsLogging.logItem("PLAY_MUSIC_STOP_CURRENT");
		stopMusic();
	}
	if (toPlayMusic) {
		SoundsLogging.logItem("CLEAR_TIMEOUT_TO_PLAY_MUSIC", {toPlayMusic});
		clearTimeout(toPlayMusic.timeoutID);
	}
	const timeoutID = setTimeout(() => {
		const id = newMusic.play();
		SoundsLogging.logItem("PLAY_MUSIC_TIMEOUT_RAN", {
			newName: name,
			newMusicHowlerID: id,
			musicMuted,
			musicVolume,
		});
		newMusic.mute(musicMuted, id);
		newMusic.volume(musicVolume, id);
		currentMusic = {name, id};
		toPlayMusic = null;
	}, 500);
	toPlayMusic = {
		name,
		timeoutID,
	};
};

/**
 * Stops the active background music track, optionally after a fade-out.
 * Clears pending delayed `playMusic` start when nothing is playing yet.
 * @param {boolean} [shouldFade] If true, fades volume down over 2s then stops.
 */
const stopMusic = shouldFade => {
	if (global.IS_ON_BAEDITOR) return;
	if (!currentMusic) {
		SoundsLogging.logItem("NOTHING_TO_STOP", toPlayMusic);
		if (toPlayMusic) {
			clearTimeout(toPlayMusic.timeoutID);
			toPlayMusic = null;
		}
		return;
	}

	const oldMusic = getSound(currentMusic.name);
	SoundsLogging.logItem("STOPPING_MUSIC", {currentName: currentMusic.name});
	if (shouldFade) {
		if (!toFadeMusic) {
			SoundsLogging.logItem("START_FADING");
			oldMusic.fade(musicVolume, 0, 2000, currentMusic.id);
			const timeoutID = setTimeout(() => {
				SoundsLogging.logItem("FADEOUT_TIMEOUT_RAN", {
					currentMusicHowlerID: currentMusic.id,
				});
				oldMusic.stop(currentMusic.id);
				currentMusic = null;
				toFadeMusic = null;
			}, 2000);
			toFadeMusic = {timeoutID};
		}
	} else {
		SoundsLogging.logItem("NON_FADING_STOP", {
			currentMusicHowlerID: currentMusic.id,
		});
		oldMusic.stop(currentMusic.id);
		currentMusic = null;
		if (toFadeMusic) {
			clearTimeout(toFadeMusic.timeoutID);
			toFadeMusic = null;
		}
	}
};

/**
 * Mutes or unmutes only the currently playing music instance (Howler id path).
 * @param {boolean} value True to mute background music while keeping the Howl alive.
 */
const setMusicMuted = value => {
	if (global.IS_ON_BAEDITOR) return;
	SoundsLogging.logItem("SET_MUSIC_MUTED", {
		value,
		currentMusicHowlerID: _.get(currentMusic, "id"),
	});
	musicMuted = !!value;
	if (currentMusic) {
		const oldMusic = getSound(currentMusic.name);
		oldMusic.mute(musicMuted, currentMusic.id);
	}
};

/**
 * Sets volume for the active background music (`0`–`100` UI scale).
 * @param {number} volume Percent 0–100; applied to the current music Howl only.
 */
const setMusicVolume = volume => {
	if (global.IS_ON_BAEDITOR) return;
	SoundsLogging.logItem("SET_MUSIC_VOLUME", {
		volume,
		currentMusicHowlerID: _.get(currentMusic, "id"),
	});
	musicVolume = Math.max(0, volume / 100);
	if (currentMusic) {
		const oldMusic = getSound(currentMusic.name);
		oldMusic.volume(musicVolume, currentMusic.id);
	}
};

/**
 * @returns {boolean} True if a background music track is considered active (post-start).
 */
const isMusicPlaying = () => {
	return !!currentMusic;
};

/**
 * Logical name of the background music that is playing, or that `playMusic`
 * has scheduled but not yet started (500ms delay). Otherwise `null`.
 * @returns {string|null}
 */
const getCurrentMusicTrackName = () => {
	if (currentMusic) {
		return currentMusic.name;
	}
	if (toPlayMusic) {
		return toPlayMusic.name;
	}
	return null;
};

module.exports = {
	preloadAllSoundFiles,
	playSound,
	stopSound,
	isSoundPlaying,
	getSoundDuration,
	getCurrentMusicTrackName,
	setAllSoundsMuted,
	setAllSoundsVolume,
	playMusic,
	stopMusic,
	setMusicMuted,
	setMusicVolume,
	isMusicPlaying,
};
